import type { ApiContext } from "@/api/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";
import type { PatientConsent, PatientStatus, ProviderProfile, UUID } from "@/core/types";
import type { UpdateProviderProfileVariables } from "@/portals/provider/(profile)/api/queryFunctions/updateProviderProfile";

type UpdatePatientStatusPayload = {
  providerName?: string;
  reason?: string;
  status: PatientStatus;
  username: UUID;
};

export type UpdatePatientStatusResponse = {
  previous_status: PatientStatus;
};

export const providerRequestsTyped = {
  fetch_provider_profile: function ({
    context,
    providerId,
  }: {
    context?: ApiContext;
    providerId?: UUID;
  } = {}) {
    return httpAuth2
      .post("/v1/provider/profile_get", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(providerId !== undefined && { searchParams: { provider_id: providerId } }),
      })
      .json<{ user: ProviderProfile }>();
  },

  get_patient_consents: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: UUID;
  }) {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/get_latest_consents`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<PatientConsent[]>();
  },

  update_patient_status: function ({
    context,
    data,
    patientId,
  }: {
    context?: ApiContext;
    data: UpdatePatientStatusPayload;
    patientId: UUID;
  }) {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/update_patient_details`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json<UpdatePatientStatusResponse>();
  },

  update_profile: function ({
    context,
    data,
    providerId,
  }: {
    context?: ApiContext;
    data: UpdateProviderProfileVariables;
    providerId?: UUID;
  }) {
    return httpAuth2
      .post("/v2/provider/profile_update", {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
        ...(providerId !== undefined && { searchParams: { provider_id: providerId } }),
      })
      .json<void>();
  },
};
